import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Target, Pencil } from "lucide-react";
import { formatMoney } from "@/lib/money";
import { toast } from "sonner";

export const WeeklyGoalCard = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState("");

  const load = () => api.get("/truffl/weekly-goal").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);

  const save = async () => {
    try {
      await api.put("/truffl/weekly-goal", { amount: Number(amount || 0) });
      toast.success("Weekly goal set — every win counts toward it");
      setEditing(false);
      load();
    } catch { toast.error("Could not save goal"); }
  };

  if (!data) return null;
  const hasGoal = data.goal > 0;

  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="weekly-goal-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-500">
            <Target className="h-5 w-5" />
          </span>
          <div>
            <p className="text-sm font-medium">Get Paid This Week</p>
            {hasGoal ? (
              <p className="text-xs text-muted-foreground" data-testid="weekly-goal-progress">
                {formatMoney(data.earned)} of {formatMoney(data.goal)} weekly goal
                {data.gap > 0 ? ` — ${formatMoney(data.gap)} to go` : " — goal smashed! 🎉"}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">Set a weekly income goal — every client you win counts toward it.</p>
            )}
          </div>
        </div>
        {editing ? (
          <div className="flex items-center gap-2">
            <Input type="number" min="0" className="h-9 w-28" value={amount}
              onChange={(e) => setAmount(e.target.value)} placeholder="£500" data-testid="weekly-goal-input" />
            <Button size="sm" onClick={save} data-testid="weekly-goal-save-btn">Save</Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => { setAmount(data.goal || ""); setEditing(true); }} data-testid="weekly-goal-edit-btn">
            <Pencil className="mr-1 h-3 w-3" /> {hasGoal ? "Edit goal" : "Set goal"}
          </Button>
        )}
      </div>
      {hasGoal && (
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-secondary">
          <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-accent transition-all"
            style={{ width: `${data.pct || 0}%` }} />
        </div>
      )}
    </div>
  );
};
